// The parent index the offline store keeps beside parented rows (BL-2340): an auction's lots or
// thumbnails are one key range in `children`, and `parentOf` answers "whose child is this" cheaply.
import type { OfflineScope } from './types';
import type { OfflineStoreBackend, OfflineStoreKey, OfflineStoreWrite } from './backend';

/** What every scoped surface (records, thumbnails, outbox) is built from. */
export interface ScopeContext {
  backend: OfflineStoreBackend;
  /** Frozen, so a caller cannot redirect a handle's keys into another scope. */
  scope: Readonly<OfflineScope>;
  /** Run a task in the engine's serial order, refusing it when the handle is stale. */
  live: <T>(task: () => Promise<T>) => Promise<T>;
  iso: () => string;
}

/** One child to file under a parent (or under none). */
export interface Placement {
  childKey: string;
  parentKey: string | null;
  /**
   * The child row's size, kept on its index entry so a parent's size is read without loading a
   * row (BL-2429). Omitted means unknown.
   */
  bytes?: number;
}

/** A child's key and the size its index entry carries; null on an entry written before BL-2429. */
export interface ChildEntry {
  key: string;
  bytes: number | null;
}

/** Rows per write batch in a bulk put, so one transaction never grows without bound. */
const PUT_MANY_CHUNK = 200;

/**
 * Keep the last item for each key, so one batch never files a child under two parents.
 *
 * @param items - the items to store.
 * @param keyOf - each item's key.
 * @returns the items, last write per key wins, in first-seen order.
 */
export function lastPerKey<T>(items: T[], keyOf: (item: T) => string): T[] {
  return [...new Map(items.map((item) => [keyOf(item), item])).values()];
}

/**
 * @param items - the items to split.
 * @returns the items in batches of at most 200.
 */
export function chunks<T>(items: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += PUT_MANY_CHUNK) out.push(items.slice(i, i + PUT_MANY_CHUNK));
  return out;
}

/**
 * The index writes that file each child under its new parent and take it off its old one. Reads
 * only the small `parentOf` rows, never the payloads. Keys must be unique within `placements`.
 *
 * @param input - the scope context, the child kind and the placements.
 * @returns the writes to add to the same batch as the rows.
 */
export async function placementWrites(input: {
  ctx: ScopeContext;
  kind: string;
  placements: Placement[];
}): Promise<OfflineStoreWrite[]> {
  const { ctx, kind, placements } = input;
  const { tenantId, userId } = ctx.scope;
  const reverse = (child: string): OfflineStoreKey => [tenantId, userId, kind, child];
  const child = (parent: string, key: string): OfflineStoreKey => [tenantId, userId, kind, parent, key];
  const previous = (await ctx.backend.getMany(
    'parentOf',
    placements.map((p) => reverse(p.childKey)),
  )) as (string | undefined)[];
  return placements.flatMap(({ childKey, parentKey, bytes }, i): OfflineStoreWrite[] => {
    const from = previous[i] ?? null;
    const writes: OfflineStoreWrite[] = [];
    if (from !== null && from !== parentKey) writes.push({ type: 'delete', table: 'children', key: child(from, childKey) });
    if (parentKey === null) {
      if (from !== null) writes.push({ type: 'delete', table: 'parentOf', key: reverse(childKey) });
      return writes;
    }
    writes.push(
      { type: 'put', table: 'children', key: child(parentKey, childKey), value: bytes ?? null },
      { type: 'put', table: 'parentOf', key: reverse(childKey), value: parentKey },
    );
    return writes;
  });
}

/**
 * @param input - the scope context, the child kind and the parent.
 * @returns every child key filed under the parent, read from the `children` index only.
 */
export async function childKeys(input: { ctx: ScopeContext; kind: string; parentKey: string }): Promise<string[]> {
  const { ctx, kind, parentKey } = input;
  const { tenantId, userId } = ctx.scope;
  const keys = await ctx.backend.scanKeys('children', [tenantId, userId, kind, parentKey]);
  return keys.map((key) => String(key[4]));
}

/**
 * @param input - the scope context, the child kind and the parent.
 * @returns every child filed under the parent with the size its index entry carries, read from
 *   the `children` index only.
 */
export async function childEntries(input: { ctx: ScopeContext; kind: string; parentKey: string }): Promise<ChildEntry[]> {
  const { ctx, kind, parentKey } = input;
  const { tenantId, userId } = ctx.scope;
  const rows = await ctx.backend.scan('children', [tenantId, userId, kind, parentKey]);
  return rows.map(({ key, value }) => ({ key: String(key[4]), bytes: typeof value === 'number' ? value : null }));
}

/**
 * @param input - the scope context, the child kind, the row table, the parent, its children and
 *   how to key a child's row.
 * @returns the writes that drop every child: the rows themselves and both index entries.
 */
export function dropChildrenWrites(input: {
  ctx: ScopeContext;
  kind: string;
  table: 'records' | 'thumbnails';
  parentKey: string;
  children: string[];
  rowKey: (child: string) => OfflineStoreKey;
}): OfflineStoreWrite[] {
  const { ctx, kind, table, parentKey, children, rowKey } = input;
  const { tenantId, userId } = ctx.scope;
  return children.flatMap((child): OfflineStoreWrite[] => [
    { type: 'delete', table, key: rowKey(child) },
    { type: 'delete', table: 'children', key: [tenantId, userId, kind, parentKey, child] },
    { type: 'delete', table: 'parentOf', key: [tenantId, userId, kind, child] },
  ]);
}
